import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { MyCampaign } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PRESETS, presetIsOn, type Filters } from "../lib/campaignFilters";
import { STAGE_WORDS, campaignPath, stageTone } from "../lib/campaign";
import { loadedAt, recentDate, shortDate } from "../lib/format";
import { ExportDialog } from "../components/CampaignExport";
import { CampaignFile } from "../components/CampaignFile";
import { SequenceLine } from "../components/CampaignSequence";
import { Spinner } from "../components/Spinner";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";

/* My campaigns: the campaigns the signed-in person owns, one card each,
 * grouped by stage.
 *
 * Between dials or at the start of the day, a rep who owns campaigns needs
 * the ones being called and the Orum list for each, without hunting through
 * every campaign. James set the page on 2026-10-09: Calling first, Ready to
 * call directly under it, both with the Orum download on the card, because
 * exporting a Ready to call campaign is what starts the calling. The other
 * stages are behind a link that says what is behind it, Enriching and
 * Planning in the middle and Completed and Abandoned at the bottom, in the
 * engine's stage order. Within a stage, the campaigns follow the engine's
 * order of the work, which puts a sequence in its order, then the nearest
 * estimated start, and each card says when calling is expected to start and
 * where the campaign is in its sequence (engine migrations 256, 311, 313). Cards, against the rule that records are a table, because
 * James specified cards here and a person has a handful of campaigns, each
 * considered on its own.
 *
 * "Mine" is the engine's answer: api.my_campaign is api.campaign narrowed to
 * the caller's open ownership (engine migration 217). Nobody is hidden from
 * anything, and every campaign is still on Campaigns.
 * docs/plans/campaign-assignment.md, milestone 10. */
const COLUMNS = "id,name,label,stage,stage_order,stage_at,filters,properties,list_count,people_with_mobile,not_yet_delivered,export_count,list_at,last_export_at,where_it_stands,next_step,shape,call_on,calling_words,calling_warning,sequence_words,sequence_length,follows_name,follows_label,followed_by_name,followed_by_label,when_calling_words,enrichment_rank";

const FIRST = ["calling", "ready_to_call"];
const OTHERS = ["enriching", "draft", "completed", "abandoned"];

export function MyCampaigns({ session }: { session: SignedIn }) {
  const token = session.idToken;
  const queryClient = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const showAll = search.get("all") === "1";

  const q = useQuery({
    queryKey: ["my-campaigns"],
    queryFn: async () => {
      // Within a stage, the engine's order of the work, then the nearest
      // estimated start (engine migrations 256, 313).
      const { data, error } = await apiClient(token).from("my_campaign").select(COLUMNS)
        .order("stage_order").order("enrichment_rank", { ascending: true, nullsFirst: false })
        .order("call_on", { ascending: true, nullsFirst: false })
        .order("stage_at", { ascending: false }).limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as MyCampaign[];
    },
  });

  const all = q.data ?? [];
  const byStage = (s: string) => all.filter((c) => c.stage === s);
  const othersCount = OTHERS.map((s) => [s, byStage(s).length] as const).filter(([, n]) => n > 0);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["my-campaigns"] });

  return (
    <main className="mc">
      <h1>My campaigns</h1>
      <div className="mc-top">
        <span className="muted">{loadedAt(q.dataUpdatedAt)}</span>
        <Button size="compact-sm" onClick={refresh} loading={q.isFetching && !q.isLoading}>Refresh</Button>
      </div>

      {q.isLoading ? <p className="muted"><Spinner />Loading your campaigns.</p>
        : q.isError ? (
          <p className="refusal" role="alert">
            Your campaigns did not load. {(q.error as Error).message} <Button kind="link" size="compact-sm" onClick={() => q.refetch()}>Try again</Button>
          </p>
        )
        : !all.length ? (
          <p className="muted">
            No campaigns are assigned to you. <Link to="/campaigns">Campaigns</Link> lists every campaign, and a campaign's page assigns it.
          </p>
        ) : (
          <>
            {FIRST.map((s) => (
              <StageGroup key={s} stage={s} rows={byStage(s)} token={token} onChange={refresh}
                          empty={s === "calling" ? "None being called." : "None ready to call."} />
            ))}
            {othersCount.length ? (
              showAll ? (
                <>
                  {OTHERS.map((s) => byStage(s).length
                    ? <StageGroup key={s} stage={s} rows={byStage(s)} token={token} onChange={refresh} />
                    : null)}
                  <p><Button kind="link" onClick={() => setSearch({}, { replace: true })}>Hide other stages</Button></p>
                </>
              ) : (
                <p className="mc-more">
                  <Button kind="link" onClick={() => setSearch({ all: "1" }, { replace: true })}>Show other stages</Button>{" "}
                  <span className="muted">{othersCount.map(([s, n]) => `${n} ${STAGE_WORDS[s]}`).join(", ")}</span>
                </p>
              )
            ) : null}
          </>
        )}
    </main>
  );
}

function StageGroup({ stage, rows, token, onChange, empty }: {
  stage: string; rows: MyCampaign[]; token: string; onChange: () => void; empty?: string;
}) {
  return (
    <section className="mc-group" aria-labelledby={`mc-${stage}`}>
      <h2 id={`mc-${stage}`}>{STAGE_WORDS[stage]} <span className="muted mc-n">{rows.length.toLocaleString()}</span></h2>
      {rows.length
        ? <div className="mc-cards">{rows.map((c) => <CampaignCard key={c.name} c={c} token={token} onChange={onChange} />)}</div>
        : <p className="muted">{empty}</p>}
    </section>
  );
}

/** One campaign: what it is, its stage and since when, its size, what is new
 *  since the last export, its dates, the engine's line, and on a campaign
 *  being called or ready to call, Export the list first. It opens the
 *  campaign page's export, which starts on the Orum list, so the words are
 *  the campaign page's too. */
function CampaignCard({ c, token, onChange }: { c: MyCampaign; token: string; onChange: () => void }) {
  const [exportOpen, setExportOpen] = useState(false);
  const queryClient = useQueryClient();
  const callable = c.stage === "calling" || c.stage === "ready_to_call";
  const f = (c.filters ?? {}) as Filters;
  // The same line as the campaign page's facts (CampaignPage.tsx).
  const segment = PRESETS.find((p) => presetIsOn(f, p))?.label;
  const states = Array.isArray(f.state_in) ? (f.state_in as string[]).join(", ") : null;
  const what = [segment, states].filter(Boolean).join(" · ");
  const exported = (c.export_count ?? 0) > 0;

  return (
    <article className="mc-card" aria-labelledby={`mc-card-${c.id}`}>
      <h3 id={`mc-card-${c.id}`}><Link to={campaignPath(c.name ?? "")}>{c.label ?? c.name}</Link></h3>
      <p className="mc-stage">
        <Badge tone={stageTone(c.stage)}>{STAGE_WORDS[c.stage ?? ""] ?? c.stage}</Badge>
        {c.stage_at ? <span className="muted"> since {recentDate(c.stage_at)}</span> : null}
      </p>
      {c.calling_words ? <p className={c.calling_warning ? "refusal mc-calling" : "mc-calling"}>{c.calling_words}</p> : null}
      {(c.sequence_length ?? 1) > 1 && c.sequence_words ? <p className="mc-what"><SequenceLine c={c} /></p> : null}
      {what ? <p className="mc-what">{what}</p> : null}
      {callable ? (
        <div className="mc-action">
          <Button kind="orange" onClick={() => setExportOpen(true)}>Export the list</Button>
        </div>
      ) : null}
      {/* The engine's newest file, so the line stays when the card moves to
          Calling as its Orum list is made (engine migration 293). */}
      <CampaignFile token={token} campaign={c.name ?? ""} />
      <dl className="mc-figures">
        <div><dt>Properties</dt><dd>{(c.properties ?? c.list_count)?.toLocaleString() ?? "Not measured yet"}</dd></div>
        <div><dt>People with a mobile</dt><dd>{c.people_with_mobile?.toLocaleString() ?? "Not measured yet"}</dd></div>
        {exported ? <div><dt>New since the last export</dt><dd>{c.not_yet_delivered?.toLocaleString() ?? "Not measured yet"}</dd></div> : null}
        <div><dt>List</dt><dd>{c.list_at ? shortDate(c.list_at) : "None yet"}</dd></div>
        <div><dt>Last export</dt><dd>{c.last_export_at ? shortDate(c.last_export_at) : "Not exported yet"}</dd></div>
      </dl>
      {c.where_it_stands ? <p className="mc-line muted">{c.where_it_stands}</p> : null}
      {callable ? (
        <ExportDialog token={token} c={c} opened={exportOpen} newOnly={c.next_step === "export_new"}
                      onClose={() => setExportOpen(false)}
                      onQueued={() => { onChange(); void queryClient.invalidateQueries({ queryKey: ["campaign-file", c.name ?? ""] }); }} />
      ) : null}
    </article>
  );
}
